'use client';

import React, { useState, useEffect } from 'react';
import { Scale, Calendar } from 'lucide-react';
import { getAyanamsaComparisonApi } from '@/lib/api/client';

export default function AyanamsaComparisonCalculatorPage() {
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  const fetchAyanamsa = async (selectedDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAyanamsaComparisonApi(selectedDate);
      setData(res);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch Ayanamsa comparison');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAyanamsa(date);
  }, []);

  return (
    <main className="min-h-screen py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Header Banner */}
        <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 text-center space-y-3 shadow-xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-white/15 bg-white/10 text-xs font-mono text-[#5ab4ff]">
            <Scale className="w-3.5 h-3.5 text-[#5ab4ff]" />
            <span>PRECISION EPHEMERIS INFRASTRUCTURE</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white">
            Ayanamsa Comparison Calculator
          </h1>
          <p className="text-xs sm:text-sm text-[rgba(220,236,255,0.85)] max-w-xl mx-auto leading-[1.6]">
            Compare calculated sidereal Ayanamsa offsets side-by-side across the 3 core astronomical standards: Lahiri (Chitra Paksha), B.V. Raman, and KP (Krishnamurti Paddhati).
          </p>
        </div>

        {/* Date Selector Form */}
        <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 space-y-4 shadow-xl">
          <div className="flex flex-col sm:flex-row items-end gap-4">
            <div className="w-full sm:w-1/2 space-y-1">
              <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#5ab4ff]" />
                <span>Select Ephemeris Date</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] [color-scheme:dark]"
              />
            </div>
            <button
              onClick={() => fetchAyanamsa(date)}
              className="w-full sm:w-auto h-12 px-8 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] transition-all cursor-pointer whitespace-nowrap active:scale-[0.98]"
            >
              Compare Ayanamsas
            </button>
          </div>

          {loading && (
            <div className="text-center py-4 text-xs font-mono text-[rgba(220,236,255,0.75)] animate-pulse">
              Querying Swiss Ephemeris C-native routines (swe_get_ayanamsa_ut)...
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-rose-500/10 border border-[#ff9bb0]/30 text-[#ff9bb0] text-xs font-mono text-center">
              {error}
            </div>
          )}
        </div>

        {/* Comparison Cards Display */}
        {data && (
          <div className="space-y-6" data-testid="ayanamsa-result">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.systems.map((sys: any) => (
                <div
                  key={sys.id}
                  className="p-6 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl space-y-4 flex flex-col justify-between hover:border-white/25 transition-all shadow-xl"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#5ab4ff]">
                        {sys.id}
                      </span>
                      {sys.id === 'lahiri' && (
                        <span className="px-2 py-0.5 rounded-full bg-[#5ab4ff]/15 text-[#5ab4ff] text-[9px] font-mono font-bold border border-[#5ab4ff]/30">
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-white">
                      {sys.name}
                    </h3>
                    <p className="text-xs text-[rgba(220,236,255,0.85)] leading-[1.6]">
                      {sys.description}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.04] border border-white/12 space-y-1">
                    <span className="text-[10px] font-mono text-[rgba(220,236,255,0.7)] uppercase block">OFFSET VALUE</span>
                    <div className="text-xl font-mono font-bold text-[#b9c6ff]">
                      {sys.formatted_dms}
                    </div>
                    <div className="text-[10px] font-mono text-[rgba(220,236,255,0.7)]">
                      {sys.value_degrees}° ({sys.diff_from_lahiri_arcsec > 0 ? `+${sys.diff_from_lahiri_arcsec}` : sys.diff_from_lahiri_arcsec}&quot; vs Lahiri)
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </main>
  );
}
